import {
  getAllRoles,
  getRolePermissions as getConfigRolePermissions,
  getCoreDashboardPermissions,
  getOperationsPermissions,
  getPosPermissions,
  getCourierPermissions,
  getAiPermissions,
  getAllPosBundles,
  getPosBundlePermissions,
  isAssignableRole,
  getRole,
} from '@/lib/config-system';
import type { TeamPermissionKey as ConfigPermissionKey, TeamRole as ConfigRole } from '@/lib/config-system';

export type TeamRole = ConfigRole;

export const TEAM_ROLE_VALUES = getAllRoles() as [TeamRole, ...TeamRole[]];

export type TeamPermissionKey = ConfigPermissionKey;
export type TeamPermissions = Record<TeamPermissionKey, boolean>;

export const TEAM_PERMISSION_KEYS = [
  ...getCoreDashboardPermissions(),
  ...getOperationsPermissions(),
  ...getPosPermissions(),
  ...getCourierPermissions(),
  ...getAiPermissions(),
] as const satisfies readonly TeamPermissionKey[];

export const CORE_DASHBOARD_PERMISSION_KEYS = getCoreDashboardPermissions() as readonly TeamPermissionKey[];

export const OPERATIONS_PERMISSION_KEYS = getOperationsPermissions() as readonly TeamPermissionKey[];

export const POS_PERMISSION_KEYS = getPosPermissions() as readonly TeamPermissionKey[];

/**
 * The service-console gate. Declared in the `pos` family of
 * configs/permissions/roles.json (P5-02) so `TeamPermissionKey` and the config
 * can no longer drift apart. It stays a distinct key rather than being folded
 * into `pos.supervisor` because 82 live files test for it specifically, and
 * because it is a *stronger* grant than the POS bundle: it covers service-level
 * configuration, not just working the till.
 */
export const LEGACY_SERVICE_CONSOLE_PERMISSION: TeamPermissionKey = 'manage_service_console';

export const COURIER_PERMISSION_KEYS = getCourierPermissions() as readonly TeamPermissionKey[];

/**
 * AI workspace permission keys (AI-201). Consumed by AI-202's centralized
 * tool-eligibility engine and by the AI workspace routes (audit, memory,
 * approvals, providers). They are part of `TEAM_PERMISSION_KEYS` so that
 * `buildPermissions` / `cloneTeamPermissions` / `normalizeTeamPermissions`
 * resolve them into every access context — the team UI renders templates and
 * bundles, not raw key checkboxes, so no dead checkboxes are surfaced.
 */
export const AI_PERMISSION_KEYS = getAiPermissions() as readonly TeamPermissionKey[];

export const TEAM_ROLE_LABELS: Record<TeamRole, string> = Object.fromEntries(
  TEAM_ROLE_VALUES.map((role) => {
    const config = getRole(role);
    return [role, config?.label ?? role];
  }),
) as Record<TeamRole, string>;

export const TEAM_ROLE_DESCRIPTIONS: Record<TeamRole, string> = Object.fromEntries(
  TEAM_ROLE_VALUES.map((role) => {
    const config = getRole(role);
    return [role, config?.description ?? ''];
  }),
) as Record<TeamRole, string>;

export const LITE_IDENTITY_ROLES = ['courier'] as const satisfies readonly TeamRole[];

export function isLiteIdentityRole(role: TeamRole): boolean {
  return (LITE_IDENTITY_ROLES as readonly TeamRole[]).includes(role);
}

/**
 * Doc 64 §5 — the only roles an offline-POS business may assign on its cloud
 * account: `manager` (the "POS manager" — menus + branch details) and `editor`
 * (menu content). No viewer, no courier, no POS operator roles, no service
 * console. The owner is the owner regardless.
 */
export const OFFLINE_POS_ASSIGNABLE_ROLES = ['manager', 'editor'] as const satisfies readonly TeamRole[];

export function isOfflinePosAssignableRole(role: TeamRole): boolean {
  return (OFFLINE_POS_ASSIGNABLE_ROLES as readonly TeamRole[]).includes(role);
}

export function assertOfflinePosRoleAssignable(role: TeamRole, offlinePos: boolean): void {
  if (offlinePos && !isOfflinePosAssignableRole(role)) {
    throw new Error('Offline POS accounts can only assign Manager and Editor roles.');
  }
}

function buildPermissions(enabled: readonly TeamPermissionKey[]): TeamPermissions {
  const permissions = {} as TeamPermissions;

  for (const key of TEAM_PERMISSION_KEYS) {
    permissions[key] = enabled.includes(key);
  }

  return permissions;
}

const ALL_PERMISSION_KEYS = [...TEAM_PERMISSION_KEYS] as const;

function buildTeamRolePermissionBaseline(): Record<TeamRole, TeamPermissions> {
  const baseline = {} as Record<TeamRole, TeamPermissions>;
  for (const role of TEAM_ROLE_VALUES) {
    const perms = getConfigRolePermissions(role) as TeamPermissionKey[];
    baseline[role] = buildPermissions(perms);
  }
  return baseline;
}

export const TEAM_ROLE_PERMISSION_BASELINE: Record<TeamRole, TeamPermissions> = buildTeamRolePermissionBaseline();

export function isTeamRole(input: unknown): input is TeamRole {
  return TEAM_ROLE_VALUES.includes(input as TeamRole);
}

export function normalizeTeamRole(input: unknown): TeamRole {
  const value = typeof input === 'string' ? input.trim().toLowerCase() : '';
  if (!isTeamRole(value)) {
    throw new Error(`Invalid team role: ${String(input).slice(0, 80)}`);
  }
  return value;
}

export function cloneTeamPermissions(input?: Partial<Record<TeamPermissionKey, unknown>> | null): TeamPermissions {
  const permissions = {} as TeamPermissions;

  for (const key of TEAM_PERMISSION_KEYS) {
    permissions[key] = Boolean(input?.[key]);
  }

  return permissions;
}

export function normalizeTeamPermissions(role: TeamRole, overrides?: unknown): TeamPermissions {
  const base = cloneTeamPermissions(TEAM_ROLE_PERMISSION_BASELINE[role]);

  const raw = !overrides || typeof overrides !== 'object' || Array.isArray(overrides)
    ? null
    : (overrides as Record<string, unknown>);

  if (raw) {
    for (const key of TEAM_PERMISSION_KEYS) {
      if (typeof raw[key] === 'boolean') {
        base[key] = raw[key];
      }
    }
  }

  if (base.manage_branches) {
    for (const key of OPERATIONS_PERMISSION_KEYS) {
      if (typeof raw?.[key] !== 'boolean') {
        base[key] = true;
      }
    }
  }

  return base;
}

export function roleHasCoreDashboardAccess(role: TeamRole): boolean {
  const baseline = TEAM_ROLE_PERMISSION_BASELINE[role];
  return CORE_DASHBOARD_PERMISSION_KEYS.some((key) => baseline[key]);
}

export function permissionsHaveCoreDashboardAccess(permissions?: Partial<Record<TeamPermissionKey, unknown>> | null): boolean {
  return CORE_DASHBOARD_PERMISSION_KEYS.some((key) => Boolean(permissions?.[key]));
}

/**
 * May this person see a dashboard's REPORTING block — metric tiles, charts,
 * engagement figures?
 *
 * Home is no longer only a core-access page. P2-05 folded the retired operations
 * hub into it, and `landingHref` therefore sends an operations-only member here:
 * they hold operations keys and no core key. Before that fold they landed on the
 * operations hub, which hid every figure behind `TeamPermissionGate
 * view_analytics`. They cannot pass that gate either — the operations keys are
 * disjoint from the core ones, so nobody holds `view_analytics` without also
 * holding a core key — so "has core access" denies them exactly what the hub
 * denied them, and allows everyone the hub ever showed.
 *
 * Core access rather than `view_analytics` is deliberate. The two agree for that
 * audience, but `view_analytics` would ALSO strip the reporting block from a
 * member who holds some other core key and not that one. That is a defensible
 * tighter rule, and it is the one a section gated per-panel uses
 * (`TodaySectionGated`), but adopting it for the whole block is a visible
 * product change nobody asked for, so it is not this function's job. Gate the
 * individual panels on `permissionsHaveAnalyticsAccess`; gate the block on this.
 *
 * The owner and the business's own creator are not team members and carry no
 * permission map at all, so both are admitted by identity rather than by key —
 * `Boolean(business.owner_id)` would admit every member, since every business
 * has an owner.
 */
export function memberMaySeeReporting(identity: {
  isOwner?: boolean | null;
  isTeamMember?: boolean | null;
  permissions?: Partial<Record<TeamPermissionKey, unknown>> | null;
}): boolean {
  if (identity.isOwner) return true;
  if (!identity.isTeamMember) return true;
  return permissionsHaveCoreDashboardAccess(identity.permissions);
}

export function permissionsHavePosAccess(permissions?: Partial<Record<TeamPermissionKey, unknown>> | null): boolean {
  return (
    POS_PERMISSION_KEYS.some((key) => Boolean(permissions?.[key])) ||
    Boolean(permissions?.[LEGACY_SERVICE_CONSOLE_PERMISSION])
  );
}

/**
 * `view_analytics` is one key, not a config group, so it has no
 * `get*Permissions()` bundle to read the way the other accessors do — and it
 * cannot be folded into `permissionsHaveCoreDashboardAccess`, because that
 * answers true for anyone holding any core key, and `view_analytics` is only
 * one of them. A member who can edit menus but not read the books has core
 * access and not this one.
 *
 * The reporting gate is this single key, and it is the gate
 * `/dashboard/operations` used. `permissionsHaveCoreDashboardAccess` therefore
 * deliberately does NOT imply it.
 */
export function permissionsHaveAnalyticsAccess(
  permissions?: Partial<Record<TeamPermissionKey, unknown>> | null,
): boolean {
  return Boolean(permissions?.view_analytics);
}

export function permissionsHaveCourierAccess(permissions?: Partial<Record<TeamPermissionKey, unknown>> | null): boolean {
  return COURIER_PERMISSION_KEYS.some((key) => Boolean(permissions?.[key]));
}

function buildPosPermissionBundleNames(): string[] {
  return getAllPosBundles();
}

export const POS_PERMISSION_BUNDLE_NAMES = buildPosPermissionBundleNames() as readonly string[];

export type PosPermissionBundleName = (typeof POS_PERMISSION_BUNDLE_NAMES)[number];

function buildPosPermissionBundles(): Record<PosPermissionBundleName, TeamPermissionKey[]> {
  const bundles = {} as Record<string, TeamPermissionKey[]>;
  for (const name of POS_PERMISSION_BUNDLE_NAMES) {
    bundles[name] = getPosBundlePermissions(name) as TeamPermissionKey[];
  }
  return bundles as Record<PosPermissionBundleName, TeamPermissionKey[]>;
}

export const POS_PERMISSION_BUNDLES: Record<PosPermissionBundleName, TeamPermissionKey[]> = buildPosPermissionBundles();

export function isPosPermissionBundleName(input: unknown): input is PosPermissionBundleName {
  return typeof input === 'string' && (POS_PERMISSION_BUNDLE_NAMES as readonly string[]).includes(input);
}

function buildLegacyNonassignableRoles(): string[] {
  return TEAM_ROLE_VALUES.filter((role) => !isAssignableRole(role));
}

export const LEGACY_NONASSIGNABLE_ROLES = buildLegacyNonassignableRoles() as readonly string[];

export type LegacyNonassignableRole = (typeof LEGACY_NONASSIGNABLE_ROLES)[number];

export function isLegacyNonassignableRole(role: TeamRole): role is LegacyNonassignableRole {
  return (LEGACY_NONASSIGNABLE_ROLES as readonly string[]).includes(role);
}

export function assertRoleAssignable(role: TeamRole): void {
  if (isLegacyNonassignableRole(role)) {
    throw new Error(
      `Role "${role}" is retired and can no longer be assigned. Migrate existing members to a permission bundle instead.`,
    );
  }
}

export function legacyRoleSuggestedBundle(role: TeamRole): PosPermissionBundleName | null {
  if (role === 'service_console_manager') {
    return 'shift_lead';
  }
  return null;
}

export function resolveBundlePermissions(bundle: PosPermissionBundleName): TeamPermissions {
  const grants: Partial<Record<TeamPermissionKey, unknown>> = {};
  for (const key of POS_PERMISSION_BUNDLES[bundle]) {
    grants[key] = true;
  }
  return cloneTeamPermissions(grants);
}
